import { ArrowUpDown, Percent, MousePointerClick } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useTranslation } from 'react-i18next';
import { formatInteger, formatSignedPercent } from '@/lib/language';

interface PerformanceSigKpiCardsProps {
  /**
   * Produkte, deren Pre/Post-Unterschied das Rauschen schlaegt.
   *
   * RICHTUNGSLOS: das Gate von `get_optimization_impact_v2` ist ZWEISEITIG
   * (`|z| >= 1.96`), diese Menge enthaelt also Rueckgaenge. Die Zahl beantwortet
   * "wie viele Messungen sind belastbar", NICHT "wie oft war es besser" — genau
   * diese Verwechslung stand hier bis Welle 10 als "Bewiesene Verbesserungen"
   * ueber der Kachel, waehrend der Tabellenkopf zwei Handbreit darunter
   * dieselbe Zahl korrekt "signifikante Aenderungen" nannte.
   */
  significantCount: number;
  /**
   * Die Richtung, aufgeteilt: Vorzeichen der signifikanten Deltas.
   *
   * Beide Zahlen tragen dasselbe Gewicht. Waere `improvedCount` allein die
   * Kopfzahl der Kachel, fuehrte die Karte weiter mit der guten Nachricht und
   * schoebe die schlechte in die Fussnote — und die Kachel beschriebe eine
   * andere Grundmenge als die beiden Lift-Kacheln daneben (die ueber ALLE
   * signifikanten Deltas rechnen). Ein Datenwerkzeug muss zeigen koennen, dass
   * es nichts Gutes zu berichten hat.
   */
  improvedCount: number;
  declinedCount: number;
  medianLiftPct: number | null;
  clickWeightedLiftPct: number | null;
  /**
   * Vergleichbare Produkte (alle Datentore bestanden, Signifikanz offen).
   *
   * 2.7 verlangt an jeder Aggregatzahl ihre Bezugsgroesse: "0 bewiesene
   * Verbesserungen" allein laesst offen, ob null von null oder null von
   * vierhundert gemessen wurde — das ist der Unterschied zwischen "noch keine
   * Daten" und "gemessen und nichts gefunden" (F19). Dieselbe Zahl traegt die
   * Begruendung der leeren Kacheln (F16).
   */
  trustworthyCount: number;
}

/**
 * Farbe aus dem VORZEICHEN, und auf beiden Seiten das `-ink`-Token.
 *
 * `text-destructive` war hier die Fehlerfarbe als SCHRIFT: 3.78:1 auf `--card`
 * bei 12 bis 24 px, waehrend die gruene Seite mit `--success-ink` bei 6.42:1
 * lag. AA verlangt 4.5:1 fuer normalen Text — die rote Haelfte der Kachelreihe
 * war also durchgefallen, und zwar exakt so wie im Stabilitaets-Chip.
 *
 * REICHWEITE DIESER REGEL, damit der Kommentar nicht mehr behauptet als der
 * Test haelt: erzwungen ist sie fuer die VIER vorzeichenfuehrenden Flaechen der
 * Wirkungs-Seite — diese Kachelreihe, `StabilityChip`, `ExperimentCard` und
 * `PerformanceDashboard` (Aenderungs-Zelle plus Filterpille). Der statische
 * Scan laeuft ausschliesslich ueber `IMPACT_SURFACE` in
 * `directionHonesty.sweep.test.tsx`, also genau ueber diese Flaechen.
 *
 * Projektweit gilt sie NICHT. `-ink` als Schriftrot benutzen zwar schon
 * `FeedCheckHeader`, `ScoreLiftBar`, `JourneyChips` und `WorkbenchProgress`, aber
 * das Flaechen-Token faerbt daneben weiterhin an 14 Stellen echte Schrift
 * (u.a. `UsageWidget`, `JobHealthIndicator`, `UploadSuccessDialog`,
 * `NotificationHistory`, `ui/alert`, `ui/form`) — ungeprueft und bewusst nicht
 * Teil dieser Welle. Nachgerechnet in `src/lib/contrast.test.ts`.
 */
function liftColor(v: number | null): string {
  if (v === null) return 'text-foreground';
  if (v > 0) return 'text-success-ink';
  if (v < 0) return 'text-destructive-ink';
  return 'text-foreground';
}

export function PerformanceSigKpiCards({
  significantCount,
  improvedCount,
  declinedCount,
  medianLiftPct,
  clickWeightedLiftPct,
  trustworthyCount,
}: PerformanceSigKpiCardsProps) {
  const { t, i18n } = useTranslation('performance');
  const language = i18n?.language;
  // Grundmenge der BEIDEN Lift-Kacheln, nicht die Verbesserungszahl: Median und
  // Klick-Gewichtung laufen ueber alle signifikanten Deltas, Rueckgaenge
  // eingeschlossen. Ein `n` aus der Verbesserungszahl schoebe die Fehl-Etikettierung
  // nur eine Zeile weiter.
  const nSuffix = significantCount > 0 ? ` (n=${significantCount})` : '';
  // `count` waehlt die Pluralform, `formatted` ist die Anzeige: i18next
  // interpoliert `{{count}}` roh und schriebe "3769" mitten in eine Seite, die
  // eine Zeile weiter "3.769" sagt (F18-Defekt in der eigenen Korrektur).
  const basis = { count: trustworthyCount, formatted: formatInteger(trustworthyCount, language) };
  // F16: ein stummer Strich erklaert seine Leere nicht. Der Grund steht sonst
  // nur in der A/B-Karte darueber, also ausserhalb der Kachel, die ihn braucht.
  const noDataHint = t('sigKpis.noData', basis);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <Card>
        <CardContent className="p-4 text-center">
          {/* `ArrowUpDown` statt des frueheren `CheckCircle2`: ein Haken behauptet
              nonverbal "bestanden/gut" und wiederholte damit genau die
              Richtungsaussage, die die Ueberschrift nicht mehr macht. */}
          <div className="text-xs text-muted-foreground mb-1 flex items-center justify-center gap-1">
            <ArrowUpDown className="h-3 w-3" /> {t('sigKpis.reliable')}
          </div>
          <div className="text-2xl font-bold text-foreground tabular-nums">{significantCount}</div>
          {/* Wortgleich mit dem Tabellenkopf darunter (`comparison.improved` /
              `.declined`), damit die beiden Stellen, die dieselben Zahlen nennen,
              sich nicht widersprechen. */}
          {significantCount > 0 && (
            <div className="flex items-center justify-center gap-2 text-xs tabular-nums">
              <span className="text-success-ink">{t('comparison.improved', { count: improvedCount })}</span>
              <span className="text-destructive-ink">{t('comparison.declined', { count: declinedCount })}</span>
            </div>
          )}
          <span className="text-xs text-muted-foreground">
            {t('sigKpis.reliableHint', basis)}
          </span>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4 text-center">
          {/* `Percent` statt des frueheren `TrendingUp`: derselbe Fehler wie der
              Haken eine Kachel zuvor, nur nonverbal. Ein Aufwaertspfeil zeigt
              nach oben, waehrend `formatSignedPercent` zwei Zeichen darunter
              "-8,4 %" schreibt — die Kachel widersprach sich selbst. Das
              Symbol nennt jetzt die EINHEIT (Prozent), die in beide Richtungen
              gilt, statt eine Richtung zu behaupten. `JUDGING_ICON_PATTERN` im
              Test dieser Datei fuehrte `trending-(up|down)` schon als
              urteilendes Symbol, wurde bis zu diesem Nachfasser aber nur auf
              die erste Kachel angewendet. */}
          <div className="text-xs text-muted-foreground mb-1 flex items-center justify-center gap-1">
            <Percent className="h-3 w-3" /> {t('sigKpis.medianLift')}
          </div>
          <div className={`text-2xl font-bold tabular-nums ${liftColor(medianLiftPct)}`}>
            {formatSignedPercent(medianLiftPct, language)}
          </div>
          <span className="text-xs text-muted-foreground">
            {medianLiftPct === null ? noDataHint : t('sigKpis.medianHint') + nSuffix}
          </span>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4 text-center">
          <div className="text-xs text-muted-foreground mb-1 flex items-center justify-center gap-1">
            <MousePointerClick className="h-3 w-3" /> {t('sigKpis.weightedLift')}
          </div>
          <div className={`text-2xl font-bold tabular-nums ${liftColor(clickWeightedLiftPct)}`}>
            {formatSignedPercent(clickWeightedLiftPct, language)}
          </div>
          <span className="text-xs text-muted-foreground">
            {clickWeightedLiftPct === null ? noDataHint : t('sigKpis.weightedHint') + nSuffix}
          </span>
        </CardContent>
      </Card>
    </div>
  );
}
